import React, { useState, useEffect } from 'react';
import Calculator from '../components/Calculator';
import Input from '../components/Input';
import { 
  DollarSign, 
  Calculator as CalculatorIcon, 
  Calendar, 
  Percent, 
  PieChart, 
  Info, 
  CreditCard, 
  TrendingDown 
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import SEOHead from '../components/SEOHead';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';

const CarLoanEMICalculator: React.FC = () => {
  // State for form inputs
  const [loanAmount, setLoanAmount] = useState('30000');
  const [interestRate, setInterestRate] = useState('5.99');
  const [loanTerm, setLoanTerm] = useState('5');
  const [downPayment, setDownPayment] = useState('0');
  const [processingFee, setProcessingFee] = useState('0');
  const [paymentFrequency, setPaymentFrequency] = useState('monthly');
  
  // State for calculation results
  const [emi, setEmi] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amortizationSchedule, setAmortizationSchedule] = useState<Array<{
    period: number;
    payment: number;
    principal: number;
    interest: number;
    balance: number;
  }>>([]);
  const [chartData, setChartData] = useState<Array<{
    name: string;
    principal: number;
    interest: number;
  }>>([]);

  // Calculate EMI on input change
  useEffect(() => {
    calculateEMI();
  }, [loanAmount, interestRate, loanTerm, downPayment, processingFee, paymentFrequency]);

  // Calculate EMI (Equated Monthly Installment)
  const calculateEMI = () => {
    const principal = parseFloat(loanAmount) - parseFloat(downPayment || '0');
    const fee = parseFloat(processingFee || '0');
    const totalPrincipal = principal + fee;
    
    if (isNaN(totalPrincipal) || totalPrincipal <= 0) return;
    
    const ratePerPeriod = parseFloat(interestRate) / 100 / 12;
    const totalPeriods = parseFloat(loanTerm) * 12;
    
    if (ratePerPeriod === 0) {
      // Simple division for 0% interest
      const monthlyPayment = totalPrincipal / totalPeriods;
      setEmi(monthlyPayment);
      setTotalInterest(0);
      setTotalAmount(totalPrincipal);
      
      generateAmortizationSchedule(totalPrincipal, 0, totalPeriods, monthlyPayment);
    } else {
      // Standard EMI formula: P * r * (1+r)^n / ((1+r)^n - 1)
      const emiValue = totalPrincipal * ratePerPeriod * Math.pow(1 + ratePerPeriod, totalPeriods) / 
                      (Math.pow(1 + ratePerPeriod, totalPeriods) - 1);
      
      setEmi(emiValue);
      setTotalInterest(emiValue * totalPeriods - totalPrincipal);
      setTotalAmount(emiValue * totalPeriods);
      
      generateAmortizationSchedule(totalPrincipal, ratePerPeriod, totalPeriods, emiValue);
    }
  };

  // Generate amortization schedule
  const generateAmortizationSchedule = (
    principal: number, 
    ratePerPeriod: number, 
    totalPeriods: number, 
    monthlyPayment: number
  ) => {
    let balance = principal;
    const schedule = [];
    let yearlyPrincipal = 0;
    let yearlyInterest = 0;
    let yearChartData = [];
    
    for (let period = 1; period <= totalPeriods; period++) {
      const interestPayment = balance * ratePerPeriod;
      const principalPayment = monthlyPayment - interestPayment;
      
      balance -= principalPayment;
      
      // Handle final payment rounding issues
      if (period === totalPeriods) {
        balance = 0;
      }
      
      schedule.push({
        period,
        payment: monthlyPayment,
        principal: principalPayment,
        interest: interestPayment,
        balance: balance < 0 ? 0 : balance
      });
      
      // Accumulate yearly data for the chart
      yearlyPrincipal += principalPayment;
      yearlyInterest += interestPayment;
      
      if (period % 12 === 0 || period === totalPeriods) {
        yearChartData.push({
          name: `Year ${Math.ceil(period / 12)}`,
          principal: yearlyPrincipal,
          interest: yearlyInterest
        });
        yearlyPrincipal = 0;
        yearlyInterest = 0;
      }
    }
    
    setAmortizationSchedule(schedule);
    setChartData(yearChartData);
  };

  // Format currency
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Handle payment frequency change (for display purposes)
  const getAdjustedPayment = (): string => {
    if (emi === 0) return formatCurrency(0);
    
    switch(paymentFrequency) {
      case 'weekly':
        return formatCurrency(emi * 12 / 52);
      case 'fortnightly':
        return formatCurrency(emi * 12 / 26);
      case 'monthly':
      default:
        return formatCurrency(emi);
    }
  };

  return (
    <>
      <SEOHead
        title="Car Loan EMI Calculator Australia | Calculate Monthly Car Repayments"
        description="Calculate your car loan EMI (Equated Monthly Installment) with our Australian car finance calculator. See your repayment schedule and total interest costs."
        keywords="car loan EMI calculator Australia, equated monthly installment, car finance EMI, monthly car payment calculator, car loan repayment schedule, Australian car loan calculator"
      />
      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FinancialProduct",
              "name": "Car Loan EMI Calculator",
              "description": "Calculate your equated monthly installments (EMI) for car loans in Australia.",
              "category": "LoanOrCredit",
              "feesAndCommissionsSpecification": "Free online calculator",
              "areaServed": {
                "@type": "Country",
                "name": "Australia"
              },
              "interactivityType": "active"
            }
          `}
        </script>
      </Helmet>
      <Calculator
        title="Australian Car Loan EMI Calculator"
        description="Calculate your Equated Monthly Installment (EMI) for car loans in Australia. An EMI comprises both principal and interest components, payable every month until your loan is fully repaid."
        icon={CalculatorIcon}
        result={emi > 0 ? `${paymentFrequency.charAt(0).toUpperCase() + paymentFrequency.slice(1)} Payment: ${getAdjustedPayment()}` : undefined}
      >
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-md mb-6">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
              <p className="text-sm text-blue-700">
                An EMI (Equated Monthly Installment) is a fixed payment amount made by a borrower to a lender on a specified date each month. EMIs are used to pay off both the interest and principal each month, ensuring the loan is paid off by the end of its term.
              </p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Loan Amount */}
            <Input
              label="Loan Amount"
              value={loanAmount}
              onChange={setLoanAmount}
              unit="$"
              placeholder="e.g. 30000"
            />
            
            {/* Interest Rate */}
            <Input
              label="Interest Rate"
              value={interestRate}
              onChange={setInterestRate}
              unit="%"
              placeholder="e.g. 5.99"
            />
            
            {/* Loan Term */}
            <div className="form-control w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Loan Term (Years)</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                value={loanTerm}
                onChange={(e) => setLoanTerm(e.target.value)}
              >
                <option value="1">1 Year</option>
                <option value="2">2 Years</option>
                <option value="3">3 Years</option>
                <option value="4">4 Years</option>
                <option value="5">5 Years</option>
                <option value="6">6 Years</option>
                <option value="7">7 Years</option>
              </select>
            </div>
            
            {/* Payment Frequency (for display only) */}
            <div className="form-control w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Frequency</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                value={paymentFrequency}
                onChange={(e) => setPaymentFrequency(e.target.value)}
              >
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            
            {/* Down Payment (Optional) */}
            <Input
              label="Down Payment (Optional)"
              value={downPayment}
              onChange={setDownPayment}
              unit="$"
              placeholder="e.g. 5000"
            />
            
            {/* Processing Fee (Optional) */}
            <Input
              label="Processing Fee (Optional)"
              value={processingFee}
              onChange={setProcessingFee}
              unit="$"
              placeholder="e.g. 300"
            />
          </div>

          {/* Results Section */}
          {emi > 0 && (
            <div className="mt-8 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="bg-blue-600 text-white px-6 py-4">
                <h3 className="text-xl font-semibold">Loan Summary</h3>
              </div>
              
              <div className="p-6">
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-gray-500 text-sm mb-1">{paymentFrequency.charAt(0).toUpperCase() + paymentFrequency.slice(1)} Payment</div>
                    <div className="text-xl font-bold text-blue-600">{getAdjustedPayment()}</div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-gray-500 text-sm mb-1">Total Interest</div>
                    <div className="text-xl font-bold text-blue-600">{formatCurrency(totalInterest)}</div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-gray-500 text-sm mb-1">Total Amount</div>
                    <div className="text-xl font-bold text-blue-600">{formatCurrency(totalAmount)}</div>
                  </div>
                </div>
                
                {/* Chart */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-4">Payment Breakdown</h4>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={chartData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={(value) => `$${value.toLocaleString()}`} />
                        <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, ""]} />
                        <Legend />
                        <Bar dataKey="principal" name="Principal" stackId="a" fill="#3B82F6" />
                        <Bar dataKey="interest" name="Interest" stackId="a" fill="#93C5FD" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                {/* Amortization Schedule */}
                <div>
                  <h4 className="text-lg font-semibold mb-4">Amortization Schedule</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Principal</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interest</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining Balance</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {amortizationSchedule.slice(0, 12).map((row) => (
                          <tr key={row.period}>
                            <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">{row.period}</td>
                            <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.payment)}</td>
                            <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.principal)}</td>
                            <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.interest)}</td>
                            <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.balance)}</td>
                          </tr>
                        ))}
                        {amortizationSchedule.length > 12 && (
                          <tr>
                            <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                              Showing first year only. Full schedule available upon request.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Educational Section */}
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Understanding Car Loan EMI</h3>
            <p className="text-gray-600 mb-4">
              An Equated Monthly Installment (EMI) is the fixed amount you pay each month towards your car loan. Each EMI payment consists of two parts:
            </p>
            <ul className="list-disc pl-5 space-y-1 mb-4">
              <li className="text-gray-600"><span className="font-medium">Principal component:</span> The portion that reduces your outstanding loan amount</li>
              <li className="text-gray-600"><span className="font-medium">Interest component:</span> The cost of borrowing, calculated on the remaining principal</li>
            </ul>
            
            <div className="bg-white p-4 rounded-lg mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">How EMI is Calculated</h4>
              <p className="text-gray-600 mb-2">The formula used to calculate EMI is:</p>
              <div className="bg-blue-50 p-3 rounded text-center mb-2">
                <span className="font-mono">EMI = P × r × (1 + r)^n / ((1 + r)^n - 1)</span>
              </div>
              <ul className="text-sm text-gray-600">
                <li><strong>P</strong>: Principal loan amount</li>
                <li><strong>r</strong>: Monthly interest rate (annual rate ÷ 12 ÷ 100)</li>
                <li><strong>n</strong>: Total number of monthly payments (loan term in years × 12)</li>
              </ul>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Factors Affecting Your EMI</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li className="text-gray-600">Loan amount (higher loan = higher EMI)</li>
                  <li className="text-gray-600">Interest rate (higher rate = higher EMI)</li>
                  <li className="text-gray-600">Loan term (longer term = lower EMI but more total interest)</li>
                  <li className="text-gray-600">Down payment (larger down payment = lower EMI)</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Tips for Managing Car Loan EMIs</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li className="text-gray-600">Compare loan options from different lenders</li>
                  <li className="text-gray-600">Make a larger down payment if possible</li>
                  <li className="text-gray-600">Choose a loan term that balances affordable EMIs with total interest costs</li>
                  <li className="text-gray-600">Consider making extra payments to reduce the principal faster</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Calculator>
    </>
  );
};

export default CarLoanEMICalculator; 